/**
 * VÉRAULT Portal — Content Studio · Media Library
 *
 * Quiet luxury media library:
 *   · Left rail: Type · Folders (with counts) · Destination tags (with counts)
 *   · Top: search + view toggle + upload zone (drag/drop)
 *   · Grid: uniform aspect-square thumbnails, hover overlay (copy URL / edit / delete)
 *   · Detail drawer: full preview + edit folder, destination, alt text, tags
 *   · "Move to folder" / "Tag destination" supports a free-text value or a
 *     suggestion from current folders (creates new on the fly).
 *
 * Backed by the additive /api/cms/media{,/folders,/destinations} +
 * PATCH /api/cms/media/:id endpoints (Phase 3 backend).
 */
import React from "react";
import {
  Upload,
  Search,
  X,
  Trash2,
  Copy,
  Check,
  Folder,
  FolderOpen,
  MapPin,
  Images,
  FileText,
  Filter,
  Pencil,
  Plus,
  CheckSquare,
  Square,
  Move,
  Tags,
  Star,
  Link as LinkIcon,
} from "lucide-react";
import {
  Surface,
  EditorialRule,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
} from "../../../index";
import { RelationalPicker } from "../../../components/RelationalPicker";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const TYPE_FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "image", label: "Images", match: (m) => m.media_type === "image" },
  { id: "pdf", label: "PDFs", match: (m) => m.media_type === "pdf" },
  { id: "document", label: "Documents", match: (m) => m.media_type === "document" },
];

const UNFILED = "__unfiled__";
const ALL_FOLDERS = "__all__";

function prettyBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function RailRow({ active, onClick, label, count, icon: Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded text-left transition-colors duration-200 ${
        active
          ? "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground))]"
          : "text-[hsl(var(--foreground-secondary))] hover:bg-[hsl(var(--background-paper))]/60 hover:text-[hsl(var(--foreground))]"
      }`}
    >
      {Icon && <Icon className="size-3.5 opacity-80 shrink-0" strokeWidth={1.5} />}
      <span
        className={`flex-1 truncate text-[13px] tracking-tight ${
          active ? "font-serif" : "font-body"
        }`}
      >
        {label}
      </span>
      {typeof count === "number" && (
        <span
          className={`font-mono text-[10px] tabular-nums ${
            active ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]/70"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function UploadZone({ onUpload, uploading, currentFolder, currentDestination }) {
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  return (
    <div
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onUpload(Array.from(e.dataTransfer.files || []));
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer rounded-[var(--radius)] border-2 border-dashed transition-colors duration-200 px-6 py-7 text-center ${
        dragOver
          ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/5"
          : "border-[rgba(0,0,0,0.1)] hover:border-[hsl(var(--accent-gold))]/60 bg-[hsl(var(--background-paper))]/40"
      }`}
      data-testid="content-media-upload-zone"
    >
      <Upload
        className="mx-auto size-5 text-[hsl(var(--foreground-muted))]"
        strokeWidth={1.5}
      />
      <p className="mt-2.5 font-body text-[13px] text-[hsl(var(--foreground-secondary))]">
        {uploading ? "Uploading…" : "Drag files here, or click to browse"}
      </p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
        Images (JPG · PNG · WebP · GIF) · PDF · DOCX
        {(currentFolder || currentDestination) && (
          <span className="ml-2 text-[hsl(var(--accent-gold))]">
            · uploaded to{" "}
            {currentFolder ? `"${currentFolder}"` : ""}
            {currentFolder && currentDestination ? " · " : ""}
            {currentDestination ? `#${currentDestination}` : ""}
          </span>
        )}
      </p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.docx"
        className="hidden"
        onChange={(e) => onUpload(Array.from(e.target.files || []))}
        data-testid="content-media-file-input"
      />
    </div>
  );
}

function MediaTile({ item, onOpen, onCopy, copied, selectMode, selected, onToggleSelect }) {
  const handleClick = () => {
    if (selectMode) onToggleSelect(item.id);
    else onOpen(item);
  };
  return (
    <div
      className={`group relative rounded overflow-hidden border bg-[hsl(var(--background-paper))] transition-shadow ${
        selected
          ? "border-[hsl(var(--accent-gold))] shadow-soft-lift ring-1 ring-[hsl(var(--accent-gold))]/40"
          : "border-[rgba(0,0,0,0.06)] hover:shadow-soft-lift"
      }`}
      data-testid={`content-media-tile-${item.id}`}
    >
      <button
        onClick={handleClick}
        className="block w-full aspect-square text-left"
      >
        {item.media_type === "image" ? (
          <img
            src={`${API_BASE}${item.file_url}`}
            alt={item.alt_text || item.original_filename}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-500 ${
              selectMode ? "" : "group-hover:scale-[1.02]"
            } ${selected ? "opacity-90" : ""}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[hsl(var(--background-stone))]/60">
            <FileText
              className="size-8 text-[hsl(var(--foreground-muted))]"
              strokeWidth={1}
            />
          </div>
        )}
      </button>
      {/* Bulk-select checkbox */}
      {selectMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
          aria-label={selected ? "Deselect" : "Select"}
          data-testid={`content-media-select-${item.id}`}
          className="absolute top-1.5 left-1.5 size-7 inline-flex items-center justify-center rounded-full bg-white/95 backdrop-blur-sm shadow"
        >
          {selected ? (
            <CheckSquare className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          ) : (
            <Square className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          )}
        </button>
      )}
      {/* Featured indicator (top-left, hidden during select mode) */}
      {!selectMode && item.featured && (
        <span
          className="absolute top-1.5 left-1.5 size-7 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow text-[hsl(var(--accent-gold))]"
          data-testid={`content-media-featured-${item.id}`}
          title="Featured"
        >
          <Star className="size-3.5 fill-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        </span>
      )}
      <div className="px-2.5 py-2 border-t border-[rgba(0,0,0,0.04)]">
        <p
          className="font-body text-[12px] text-[hsl(var(--foreground))] truncate"
          title={item.original_filename}
        >
          {item.original_filename}
        </p>
        <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-[hsl(var(--foreground-muted))] truncate">
          {item.folder ? (
            <span className="inline-flex items-center gap-1 text-[hsl(var(--foreground-secondary))]">
              <Folder className="size-2.5" strokeWidth={1.5} /> {item.folder}
            </span>
          ) : null}
          {item.destination_tag ? (
            <span className="inline-flex items-center gap-1 text-[hsl(var(--accent-gold))]">
              <MapPin className="size-2.5" strokeWidth={1.5} /> {item.destination_tag}
            </span>
          ) : null}
          {!item.folder && !item.destination_tag && <span>{prettyBytes(item.size_bytes)}</span>}
        </div>
      </div>
      {!selectMode && (
        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy(item);
            }}
            title="Copy URL"
            data-testid={`content-media-copy-${item.id}`}
            className="size-7 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--foreground))]"
          >
            {copied ? (
              <Check className="size-3.5 text-[hsl(var(--accent-sage))]" strokeWidth={1.5} />
            ) : (
              <Copy className="size-3.5" strokeWidth={1.5} />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen(item);
            }}
            title="Edit"
            data-testid={`content-media-edit-${item.id}`}
            className="size-7 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--foreground))]"
          >
            <Pencil className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}

function DetailDrawer({ item, onClose, onSave, onDelete, folders, destinations }) {
  const [folder, setFolder] = React.useState(item?.folder || "");
  const [destination, setDestination] = React.useState(item?.destination_tag || "");
  const [altText, setAltText] = React.useState(item?.alt_text || "");
  const [tags, setTags] = React.useState((item?.tags || []).join(", "));
  const [featured, setFeatured] = React.useState(!!item?.featured);
  const [linkedJourneys, setLinkedJourneys] = React.useState(item?.linked_journey_ids || []);
  const [linkedHotels, setLinkedHotels] = React.useState(item?.linked_hotel_tags || []);
  const [linkedJournals, setLinkedJournals] = React.useState(item?.linked_journal_ids || []);
  const [campaignIds, setCampaignIds] = React.useState(item?.campaign_ids || []);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setFolder(item?.folder || "");
    setDestination(item?.destination_tag || "");
    setAltText(item?.alt_text || "");
    setTags((item?.tags || []).join(", "));
    setFeatured(!!item?.featured);
    setLinkedJourneys(item?.linked_journey_ids || []);
    setLinkedHotels(item?.linked_hotel_tags || []);
    setLinkedJournals(item?.linked_journal_ids || []);
    setCampaignIds(item?.campaign_ids || []);
  }, [item?.id]);

  if (!item) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item.id, {
        folder: folder.trim(),
        destination_tag: destination.trim(),
        alt_text: altText.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        featured,
        linked_journey_ids: linkedJourneys,
        linked_hotel_tags: linkedHotels,
        linked_journal_ids: linkedJournals,
        campaign_ids: campaignIds,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
      data-testid="content-media-drawer"
    >
      <div className="flex-1 bg-black/30 backdrop-blur-sm" />
      <div
        className="w-full sm:w-[460px] bg-[hsl(var(--background))] shadow-2xl overflow-y-auto border-l border-[rgba(0,0,0,0.08)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background))]/95 backdrop-blur-md">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Asset details
            </p>
            <p
              className="mt-0.5 font-serif text-base text-[hsl(var(--foreground))] truncate"
              title={item.original_filename}
            >
              {item.original_filename}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="content-media-drawer-close"
            className="size-9 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {item.media_type === "image" ? (
            <img
              src={`${API_BASE}${item.file_url}`}
              alt={item.alt_text || item.original_filename}
              className="w-full max-h-[420px] object-contain rounded bg-[hsl(var(--background-paper))]"
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center bg-[hsl(var(--background-paper))] rounded">
              <FileText
                className="size-10 text-[hsl(var(--foreground-muted))]"
                strokeWidth={1.25}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            <div>
              <p>Type</p>
              <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] normal-case tracking-tight">
                {item.media_type}
              </p>
            </div>
            <div>
              <p>Size</p>
              <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] normal-case tracking-tight">
                {prettyBytes(item.size_bytes)}
              </p>
            </div>
            <div>
              <p>Uploaded</p>
              <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] normal-case tracking-tight">
                {fmt.formatDate(item.created_at)}
              </p>
            </div>
            <div>
              <p>Public URL</p>
              <a
                href={`${API_BASE}${item.file_url}`}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--accent-gold))] normal-case tracking-tight underline-offset-2 hover:underline"
              >
                Open
              </a>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Folder
              </label>
              <input
                type="text"
                list="content-media-folders-suggest"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                placeholder="e.g. Editorial covers"
                data-testid="content-media-drawer-folder"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
              <datalist id="content-media-folders-suggest">
                {folders
                  .filter((f) => f.folder)
                  .map((f) => (
                    <option key={f.folder} value={f.folder} />
                  ))}
              </datalist>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Destination tag
              </label>
              <input
                type="text"
                list="content-media-destinations-suggest"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="e.g. italy, kenya, japan"
                data-testid="content-media-drawer-destination"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
              <datalist id="content-media-destinations-suggest">
                {destinations.map((d) => (
                  <option key={d.destination_tag} value={d.destination_tag} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Alt text
              </label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe the image for accessibility"
                data-testid="content-media-drawer-alt"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="hero, editorial, sunset"
                data-testid="content-media-drawer-tags"
                className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
            </div>

            {/* Featured toggle */}
            <button
              onClick={() => setFeatured((v) => !v)}
              data-testid="content-media-drawer-featured"
              className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12.5px] tracking-tight border transition-colors ${
                featured
                  ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/30"
                  : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <Star className="size-3.5" strokeWidth={1.5} />
              {featured ? "Featured asset" : "Mark featured"}
            </button>
          </div>

          {/* Connections — Phase 9a relational spine */}
          <div className="space-y-4 pt-4 border-t border-[rgba(0,0,0,0.06)]" data-testid="content-media-drawer-connections">
            <div className="flex items-center gap-2">
              <LinkIcon
                className="size-3 text-[hsl(var(--foreground-muted))]"
                strokeWidth={1.5}
              />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Connections
              </span>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Journeys
              </label>
              <div className="mt-1.5">
                <RelationalPicker
                  kind="journey"
                  value={linkedJourneys}
                  onChange={setLinkedJourneys}
                  testid="content-media-drawer-pick-journey"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Hotels (free-text)
              </label>
              <div className="mt-1.5">
                <RelationalPicker
                  kind="hotel"
                  value={linkedHotels}
                  onChange={setLinkedHotels}
                  testid="content-media-drawer-pick-hotel"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Journals
              </label>
              <div className="mt-1.5">
                <RelationalPicker
                  kind="journal"
                  value={linkedJournals}
                  onChange={setLinkedJournals}
                  testid="content-media-drawer-pick-journal"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                Campaigns
              </label>
              <div className="mt-1.5">
                <RelationalPicker
                  kind="campaign"
                  value={campaignIds}
                  onChange={setCampaignIds}
                  testid="content-media-drawer-pick-campaign"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[rgba(0,0,0,0.06)]">
            <button
              onClick={() => onDelete(item)}
              data-testid="content-media-drawer-delete"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] transition-colors"
            >
              <Trash2 className="size-3" strokeWidth={1.5} /> Delete asset
            </button>
            <div className="flex items-center gap-2">
              <PortalButton variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </PortalButton>
              <PortalButton
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="content-media-drawer-save"
              >
                {saving ? "Saving…" : "Save details"}
              </PortalButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ContentMedia() {
  const [media, setMedia] = React.useState(null);
  const [folders, setFolders] = React.useState([]);
  const [destinations, setDestinations] = React.useState([]);
  const [campaigns, setCampaigns] = React.useState([]);
  const [campaignFilter, setCampaignFilter] = React.useState("");
  const [featuredOnly, setFeaturedOnly] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [folderFilter, setFolderFilter] = React.useState(ALL_FOLDERS);
  const [destinationFilter, setDestinationFilter] = React.useState(""); // "" = all
  const [query, setQuery] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [copiedId, setCopiedId] = React.useState(null);
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [bulkPrompt, setBulkPrompt] = React.useState(null); // null | {kind:"folder"|"destination", value:""}

  const toggleSelectMode = () => {
    setSelectMode((m) => {
      if (m) setSelectedIds(new Set());
      return !m;
    });
  };
  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = (visibleIds) => {
    setSelectedIds(new Set(visibleIds));
  };

  const applyBulk = async (payload) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => api.patch(`/cms/media/${id}`, payload).catch(() => null)));
    setBulkPrompt(null);
    setSelectedIds(new Set());
    setSelectMode(false);
    refresh();
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} asset${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    await Promise.all(ids.map((id) => api.delete(`/cms/media/${id}`).catch(() => null)));
    setSelectedIds(new Set());
    setSelectMode(false);
    refresh();
  };

  const refresh = React.useCallback(() => {
    setMedia(null);
    Promise.all([
      api.get("/cms/media?limit=500").then((r) => r.data).catch(() => null),
      api.get("/cms/media/folders").then((r) => r.data).catch(() => []),
      api.get("/cms/media/destinations").then((r) => r.data).catch(() => []),
      api.get("/cms/campaigns").then((r) => r.data).catch(() => []),
    ]).then(([m, f, d, c]) => {
      if (m === null) {
        setError("Couldn't load the media library.");
        return;
      }
      setMedia(Array.isArray(m) ? m : []);
      setFolders(Array.isArray(f) ? f : []);
      setDestinations(Array.isArray(d) ? d : []);
      setCampaigns(Array.isArray(c) ? c : []);
    });
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = React.useMemo(() => {
    if (!media) return null;
    const tf = TYPE_FILTERS.find((t) => t.id === typeFilter) || TYPE_FILTERS[0];
    const q = query.trim().toLowerCase();
    return media.filter((m) => {
      if (!tf.match(m)) return false;
      if (folderFilter === UNFILED) {
        if ((m.folder || "").trim() !== "") return false;
      } else if (folderFilter !== ALL_FOLDERS) {
        if ((m.folder || "") !== folderFilter) return false;
      }
      if (destinationFilter) {
        if ((m.destination_tag || "") !== destinationFilter) return false;
      }
      if (campaignFilter) {
        if (!(m.campaign_ids || []).includes(campaignFilter)) return false;
      }
      if (featuredOnly && !m.featured) return false;
      if (q) {
        const hay = [m.original_filename, m.alt_text, m.folder, m.destination_tag, ...(m.tags || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [media, typeFilter, folderFilter, destinationFilter, campaignFilter, featuredOnly, query]);

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        // If a folder filter is active (and not "All"/"Unfiled"), upload there
        if (folderFilter !== ALL_FOLDERS && folderFilter !== UNFILED) {
          form.append("folder", folderFilter);
        }
        if (destinationFilter) {
          form.append("destination_tag", destinationFilter);
        }
        try {
          await api.post("/cms/media/upload", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          window.alert(`Upload failed for ${file.name}: ${err?.response?.data?.detail || err.message}`);
        }
      }
    } finally {
      setUploading(false);
      refresh();
    }
  };

  const handleSave = async (id, payload) => {
    await api.patch(`/cms/media/${id}`, payload);
    refresh();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.original_filename}"? This cannot be undone.`)) return;
    await api.delete(`/cms/media/${item.id}`);
    setSelected(null);
    refresh();
  };

  const handleCopy = async (item) => {
    const url = `${API_BASE}${item.file_url}`;
    try {
      await navigator.clipboard?.writeText(url);
    } catch {
      // Sandboxed contexts may deny clipboard permission; swallow silently.
    }
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Build rail counts
  const totalCount = media?.length ?? 0;
  const unfiledCount = (folders.find((f) => !f.folder)?.count) ?? 0;
  const activeFolderLabel =
    folderFilter === ALL_FOLDERS
      ? "All folders"
      : folderFilter === UNFILED
      ? "Unfiled"
      : folderFilter;

  return (
    <div className="space-y-6" data-testid="content-media">
      {/* Search + view toggle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[hsl(var(--foreground-muted))]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search assets…"
              data-testid="content-media-search"
              className="h-9 pl-8 pr-7 w-72 text-[13px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)] rounded-full focus:outline-none focus:border-[hsl(var(--accent-gold))]"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              >
                <X className="size-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
          <div
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]"
            data-testid="content-media-active-scope"
          >
            <Filter className="inline size-3 mb-px mr-1" strokeWidth={1.5} />
            {activeFolderLabel}
            {destinationFilter && <span className="ml-1 text-[hsl(var(--accent-gold))]">· #{destinationFilter}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="content-media-type-filter">
          <button
            onClick={toggleSelectMode}
            data-testid="content-media-select-mode"
            className={`mr-1 inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-body text-[12.5px] tracking-tight transition-colors duration-200 border ${
              selectMode
                ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/30"
                : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] border-[rgba(0,0,0,0.08)] bg-[hsl(var(--background-paper))]"
            }`}
          >
            <CheckSquare className="size-3.5" strokeWidth={1.5} />
            {selectMode ? "Done" : "Select"}
          </button>
          {TYPE_FILTERS.map((t) => {
            const count = media ? media.filter(t.match).length : 0;
            return (
              <button
                key={t.id}
                onClick={() => setTypeFilter(t.id)}
                data-testid={`content-media-type-${t.id}`}
                className={`relative px-2.5 py-1.5 font-body text-[12.5px] tracking-tight transition-colors duration-200 inline-flex items-center gap-1.5 ${
                  typeFilter === t.id
                    ? "text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                <span className={typeFilter === t.id ? "font-serif" : ""}>{t.label}</span>
                {count > 0 && (
                  <span
                    className={`font-mono text-[10px] tabular-nums ${
                      typeFilter === t.id
                        ? "text-[hsl(var(--accent-gold))]"
                        : "text-[hsl(var(--foreground-muted))]/70"
                    }`}
                  >
                    {count}
                  </span>
                )}
                <span
                  className={`absolute -bottom-px left-2 right-2 h-px ${
                    typeFilter === t.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </div>

      <UploadZone
        onUpload={handleUpload}
        uploading={uploading}
        currentFolder={folderFilter !== ALL_FOLDERS && folderFilter !== UNFILED ? folderFilter : null}
        currentDestination={destinationFilter || null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        {/* Left rail */}
        <aside className="space-y-6" data-testid="content-media-rail">
          <div>
            <p className="px-3 mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Folders
            </p>
            <div className="space-y-0.5">
              <RailRow
                active={folderFilter === ALL_FOLDERS}
                onClick={() => setFolderFilter(ALL_FOLDERS)}
                label="All folders"
                count={totalCount}
                icon={FolderOpen}
                testid="content-media-folder-all"
              />
              <RailRow
                active={folderFilter === UNFILED}
                onClick={() => setFolderFilter(UNFILED)}
                label="Unfiled"
                count={unfiledCount}
                icon={Folder}
                testid="content-media-folder-unfiled"
              />
              {folders
                .filter((f) => f.folder)
                .sort((a, b) => a.folder.localeCompare(b.folder))
                .map((f) => (
                  <RailRow
                    key={f.folder}
                    active={folderFilter === f.folder}
                    onClick={() => setFolderFilter(f.folder)}
                    label={f.folder}
                    count={f.count}
                    icon={Folder}
                    testid={`content-media-folder-${f.folder.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))}
            </div>
          </div>

          <div>
            <p className="px-3 mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Destinations
            </p>
            <div className="space-y-0.5">
              <RailRow
                active={!destinationFilter}
                onClick={() => setDestinationFilter("")}
                label="All destinations"
                icon={MapPin}
                testid="content-media-dest-all"
              />
              {destinations.length === 0 ? (
                <p className="px-3 py-2 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  Tag an asset to start the list.
                </p>
              ) : (
                destinations.map((d) => (
                  <RailRow
                    key={d.destination_tag}
                    active={destinationFilter === d.destination_tag}
                    onClick={() => setDestinationFilter(d.destination_tag)}
                    label={d.destination_tag}
                    count={d.count}
                    icon={MapPin}
                    testid={`content-media-dest-${d.destination_tag.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))
              )}
            </div>
          </div>

          {campaigns.length > 0 && (
            <div>
              <p className="px-3 mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Campaigns
              </p>
              <div className="space-y-0.5">
                <RailRow
                  active={!campaignFilter}
                  onClick={() => setCampaignFilter("")}
                  label="All campaigns"
                  icon={LinkIcon}
                  testid="content-media-camp-all"
                />
                {campaigns.map((c) => (
                  <RailRow
                    key={c.id}
                    active={campaignFilter === c.id}
                    onClick={() => setCampaignFilter(c.id)}
                    label={c.name}
                    icon={LinkIcon}
                    testid={`content-media-camp-${c.id}`}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="px-3 mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Editorial
            </p>
            <button
              onClick={() => setFeaturedOnly((v) => !v)}
              data-testid="content-media-featured-toggle"
              className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded text-left transition-colors duration-200 ${
                featuredOnly
                  ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]"
                  : "text-[hsl(var(--foreground-secondary))] hover:bg-[hsl(var(--background-paper))]/60 hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <Star className="size-3.5 opacity-80 shrink-0" strokeWidth={1.5} />
              <span
                className={`flex-1 truncate text-[13px] tracking-tight ${
                  featuredOnly ? "font-serif" : "font-body"
                }`}
              >
                Featured only
              </span>
            </button>
          </div>
        </aside>

        {/* Main grid */}
        <div className="min-w-0">
          <EditorialRule
            label={
              filtered === null
                ? "loading"
                : `${filtered.length} asset${filtered.length === 1 ? "" : "s"}`
            }
          />
          <div className="mt-5">
            {error ? (
              <Empty title={error} icon={<Images className="size-6" strokeWidth={1.25} />} />
            ) : filtered === null ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="aspect-square w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <Empty
                title={
                  query
                    ? `No assets match "${query}"`
                    : folderFilter === UNFILED
                    ? "Nothing unfiled."
                    : folderFilter !== ALL_FOLDERS
                    ? `"${folderFilter}" is empty.`
                    : destinationFilter
                    ? `Nothing tagged #${destinationFilter} yet.`
                    : "Your library is empty."
                }
                hint={
                  query
                    ? "Try clearing search or switching filters."
                    : "Drop files above to begin curating."
                }
                icon={<Images className="size-6" strokeWidth={1.25} />}
                action={
                  folderFilter !== ALL_FOLDERS && folderFilter !== UNFILED ? (
                    <PortalButton
                      size="sm"
                      variant="ghost"
                      icon={<Plus className="size-3.5" />}
                      onClick={() => setFolderFilter(ALL_FOLDERS)}
                    >
                      Show all folders
                    </PortalButton>
                  ) : null
                }
              />
            ) : (
              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                data-testid="content-media-grid"
              >
                {filtered.map((m) => (
                  <MediaTile
                    key={m.id}
                    item={m}
                    onOpen={setSelected}
                    onCopy={handleCopy}
                    copied={copiedId === m.id}
                    selectMode={selectMode}
                    selected={selectedIds.has(m.id)}
                    onToggleSelect={toggleId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global datalists — referenced by both the detail drawer and the
          bulk prompt. Kept at the top level so they exist independently
          of whether the drawer is open. */}
      <datalist id="content-media-folders-suggest">
        {folders
          .filter((f) => f.folder)
          .map((f) => (
            <option key={f.folder} value={f.folder} />
          ))}
      </datalist>
      <datalist id="content-media-destinations-suggest">
        {destinations.map((d) => (
          <option key={d.destination_tag} value={d.destination_tag} />
        ))}
      </datalist>

      {/* Bulk action toolbar — appears when items are selected */}
      {selectMode && (
        <div
          className="sticky bottom-4 z-30 mx-auto max-w-2xl"
          data-testid="content-media-bulk-toolbar"
        >
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] shadow-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
              <span className="font-serif text-base normal-case tracking-tight mr-1.5">
                {selectedIds.size}
              </span>
              selected
            </span>
            <button
              onClick={() => filtered && selectAllVisible(filtered.map((m) => m.id))}
              data-testid="content-media-bulk-select-all"
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
            >
              · Select all visible
            </button>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setBulkPrompt({ kind: "folder", value: "" })}
                disabled={selectedIds.size === 0}
                data-testid="content-media-bulk-move"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
              >
                <Move className="size-3.5" strokeWidth={1.5} /> Move to folder
              </button>
              <button
                onClick={() => setBulkPrompt({ kind: "destination", value: "" })}
                disabled={selectedIds.size === 0}
                data-testid="content-media-bulk-tag"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
              >
                <Tags className="size-3.5" strokeWidth={1.5} /> Tag destination
              </button>
              <button
                onClick={() => setBulkPrompt({ kind: "campaign", value: "" })}
                disabled={selectedIds.size === 0}
                data-testid="content-media-bulk-campaign"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
              >
                <LinkIcon className="size-3.5" strokeWidth={1.5} /> Assign campaign
              </button>
              <button
                onClick={() => applyBulk({ featured: true })}
                disabled={selectedIds.size === 0}
                data-testid="content-media-bulk-feature"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-white/10 hover:bg-white/20 disabled:opacity-40 transition-colors"
              >
                <Star className="size-3.5" strokeWidth={1.5} /> Mark featured
              </button>
              <button
                onClick={bulkDelete}
                disabled={selectedIds.size === 0}
                data-testid="content-media-bulk-delete"
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-[hsl(var(--accent-terracotta))]/80 hover:bg-[hsl(var(--accent-terracotta))] disabled:opacity-40 transition-colors"
              >
                <Trash2 className="size-3.5" strokeWidth={1.5} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk prompt modal — Move to folder / Tag destination */}
      {bulkPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setBulkPrompt(null)}
          data-testid="content-media-bulk-prompt"
        >
          <div
            className="w-full max-w-md bg-[hsl(var(--background))] rounded-[var(--radius)] shadow-2xl border border-[rgba(0,0,0,0.08)] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {bulkPrompt.kind === "folder"
                ? "Move to folder"
                : bulkPrompt.kind === "destination"
                ? "Tag destination"
                : "Assign campaign"}
            </p>
            <p className="mt-1 font-serif text-xl text-[hsl(var(--foreground))]">
              {selectedIds.size} asset{selectedIds.size === 1 ? "" : "s"}
            </p>
            <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
              {bulkPrompt.kind === "folder"
                ? "Type a new folder name or pick an existing one. Leave empty to remove from folder."
                : bulkPrompt.kind === "destination"
                ? "Type a destination tag or pick an existing one. Leave empty to clear."
                : "Pick a campaign to add to every selected asset."}
            </p>
            {bulkPrompt.kind === "campaign" ? (
              <select
                autoFocus
                value={bulkPrompt.value}
                onChange={(e) => setBulkPrompt({ ...bulkPrompt, value: e.target.value })}
                data-testid="content-media-bulk-prompt-select"
                className="mt-4 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              >
                <option value="">Choose a campaign…</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                autoFocus
                type="text"
                list={bulkPrompt.kind === "folder" ? "content-media-folders-suggest" : "content-media-destinations-suggest"}
                value={bulkPrompt.value}
                onChange={(e) => setBulkPrompt({ ...bulkPrompt, value: e.target.value })}
                placeholder={bulkPrompt.kind === "folder" ? "e.g. Editorial covers" : "e.g. italy, kenya"}
                data-testid="content-media-bulk-prompt-input"
                className="mt-4 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              />
            )}
            <div className="mt-6 flex items-center justify-end gap-2">
              <PortalButton variant="ghost" size="sm" onClick={() => setBulkPrompt(null)}>
                Cancel
              </PortalButton>
              <PortalButton
                variant="primary"
                size="sm"
                onClick={async () => {
                  if (bulkPrompt.kind === "folder") {
                    await applyBulk({ folder: bulkPrompt.value.trim() });
                  } else if (bulkPrompt.kind === "destination") {
                    await applyBulk({ destination_tag: bulkPrompt.value.trim() });
                  } else if (bulkPrompt.kind === "campaign") {
                    if (!bulkPrompt.value) return;
                    // Add campaign id to each selected asset, preserving existing ids
                    const ids = Array.from(selectedIds);
                    await Promise.all(
                      ids.map((id) => {
                        const m = media.find((x) => x.id === id);
                        const next = Array.from(
                          new Set([...(m?.campaign_ids || []), bulkPrompt.value]),
                        );
                        return api.patch(`/cms/media/${id}`, { campaign_ids: next }).catch(() => null);
                      }),
                    );
                    setBulkPrompt(null);
                    setSelectedIds(new Set());
                    setSelectMode(false);
                    refresh();
                  }
                }}
                disabled={bulkPrompt.kind === "campaign" && !bulkPrompt.value}
                data-testid="content-media-bulk-prompt-apply"
              >
                Apply to {selectedIds.size}
              </PortalButton>
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          folders={folders}
          destinations={destinations}
        />
      )}
    </div>
  );
}
